<!-- Component can be used in microscope and periscope -->
<template>
  <div class="date-picker" :class="{ 'is-disabled': disabled }">
    <div class="header">
      <button class="arrow left" type="button" :disabled="disabled" @click="previousMonth">
        <ArrowRight :size="21" />
      </button>
      <span class="current-month">
        {{ currentMonth }}
      </span>
      <button class="arrow" type="button" :disabled="disabled" @click="nextMonth">
        <ArrowRight :size="21" />
      </button>
    </div>
    <div class="calendar">
      <div class="weekdays">
        <span v-for="(day, index) in weekdays" :key="day + index">{{ day }}</span>
      </div>
      <div class="days">
        <button
          v-for="day in visibleDays"
          :key="day.id"
          type="button"
          :title="day.date"
          :disabled="disabled"
          :class="{ selected: isSelected(day.date), disabled: !day.isCurrentMonth, today: day.isToday }"
          @click="selectDate(day)"
        >
          {{ day.display }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'

import ArrowRight from '../../icons/ArrowRight.vue'

const props = defineProps<{
  // For shared components to work in both vue 2.7 and vue 3 we need to support both `value` and `modelValue`
  // When periscope is updated to vue 3, we can remove `value` and only use `modelValue`
  value?: string | null
  modelValue?: string | null
  disabled?: boolean
}>()

dayjs.extend(isoWeek)

const emit = defineEmits<{
  (e: 'input', v: string): void
  (e: 'update:modelValue', v: string): void
}>()

// For vue 2.7 and vue 3 compatibility
const currentValue = computed(() => props.modelValue ?? props.value)

const visibleMonth = ref(currentValue.value ? dayjs(currentValue.value) : dayjs())

watch(
  () => currentValue.value,
  () => {
    visibleMonth.value = currentValue.value ? dayjs(currentValue.value) : dayjs()
  },
)

const currentMonth = computed(() => visibleMonth.value.format('MMMM YYYY'))

const weekdays = ['M', 'T', 'O', 'T', 'F', 'L', 'S']

const visibleDays = computed(() => {
  const date = visibleMonth.value
  const startOfMonth = date.startOf('month')
  const endOfMonth = date.endOf('month')
  // Don't change from ('isoWeek') to ('week') — that would use the consuming app's dayjs locale,
  // which would misalign the days with the weekdays header. Both apps set 'sv' today, so it would
  // look correct until one of them doesn't.
  const startOfWeek = startOfMonth.startOf('isoWeek')
  const endOfWeek = endOfMonth.endOf('isoWeek')
  const diffInDays = endOfWeek.diff(startOfWeek, 'days') + 1

  return Array.from({ length: diffInDays }, (_, index) => {
    const day = startOfWeek.add(index, 'day')
    return {
      display: day.format('D'),
      date: day.format('YYYY-MM-DD'),
      isToday: day.isSame(dayjs(), 'day'),
      isCurrentMonth: day.isSame(date, 'month'),
      id: `day-${index}`,
    }
  })
})

const previousMonth = () => {
  if (props.disabled) return

  visibleMonth.value = visibleMonth.value.subtract(1, 'month')
}

const nextMonth = () => {
  if (props.disabled) return

  visibleMonth.value = visibleMonth.value.add(1, 'month')
}

const selectDate = (day: { display: string; date: string }) => {
  if (props.disabled) return

  emit('input', day.date)
  emit('update:modelValue', day.date)
}

const isSelected = (date: string) => {
  return currentValue.value && dayjs(date).isSame(dayjs(currentValue.value), 'day')
}
</script>

<style scoped lang="scss">
.date-picker {
  --date-picker-item-gap: 6px;
  --date-picker-item-size: 32px;

  width: 100%;
  overflow: hidden;

  background-color: var(--color-canvas-default);

  border: 1.5px solid var(--color-border-default);
  border-radius: 20px;
  padding: 12px;

  &.is-disabled {
    opacity: 0.65;
  }

  @media screen and (min-width: 600px) {
    width: fit-content;
  }

  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;

    .current-month {
      text-transform: capitalize;
      font-size: 18px;
      font-weight: 500;
    }

    .arrow {
      &.left {
        transform: rotateY(180deg);
      }
    }
  }

  .calendar {
    display: flex;
    flex-direction: column;
  }

  .weekdays {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: var(--date-picker-item-gap);
    height: var(--date-picker-item-size);
    align-items: center;
    margin-bottom: 8px;
    font-weight: 500;
    justify-items: center;

    span {
      width: var(--date-picker-item-size);
      text-align: center;
      font-size: 14px;
      color: var(--color-fg-default);
    }
  }

  .days {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: var(--date-picker-item-gap);
    justify-items: center;

    button {
      display: flex;
      align-items: center;
      justify-content: center;
      width: var(--date-picker-item-size);
      height: var(--date-picker-item-size);
      border-radius: 50%;
      cursor: pointer;
      transition: background-color 0.2s ease;
      color: var(--color-fg-default);

      &:hover {
        background-color: var(--color-overlay-hover);
      }

      &.today {
        border: 1.5px solid var(--color-border-default);
      }

      &.disabled {
        color: var(--color-disabled-emphasis);
        border-color: var(--color-disabled-emphasis);
      }

      &.selected {
        background-color: var(--color-border-default);
        color: #fff;
      }

      &:disabled {
        cursor: not-allowed;
      }
    }
  }
}
</style>
